// The process map's data layer: the render graph derived from a process's instances, and the
// statistics the map and its side panels read off it. Pure TypeScript with no React and no DOM.
// Where the graph goes on screen is processGraphLayout; how long things took is
// processDurationStats. The rendering components depend on these modules, never on each other.
import type { Process, ProcessNode } from "../../api/types";
import { type DurationObservation, median } from "./processDurationStats";
import { caseDay } from "./processInstanceLabels";

export const START_NODE_ID = "start:start";
export const END_NODE_ID = "end:end";

const TOP_TRANSITION_WAIT_MIN_SECONDS = 2 * 60;
const USUAL_STEP_MIN_SHARE = 0.1;
const USUAL_STEP_LIMIT = 5;
// Two usual steps are joined when this many cases do both, or this share of all cases if more,
// and this share of those cases do them in the joined order.
const USUAL_ORDER_MIN_CASES = 5;
const USUAL_ORDER_MIN_CASE_SHARE = 0.05;
const USUAL_ORDER_MIN_AGREEMENT = 0.75;

export const truncate = (s: string, n: number): string =>
  s.length > n ? `${s.slice(0, n - 1).trim()}…` : s;

export const edgeKey = (from: string, to: string) => `${from}->${to}`;

export type ProcessInstance = Process["instances"][number];
export type ProcessStep = ProcessInstance["steps"][number];
export type ProcessObject = ProcessStep["objects"][number];

export type ProcessNodeOccurrence = {
  instanceId: string;
  stepIndex: number;
  duration: number;
  description: string;
  /** The local day its case started on, which the duration trend places it by. */
  day?: string;
  instanceStart?: string;
  // When this very occurrence started, from the step's own activity coverage. Absent on terminals
  // and on analyses without coverage; `instanceStart` is the fallback, but only that — two
  // occurrences of one step in one instance are hours apart and must not share a timestamp.
  stepStart?: string;
  objects: ProcessObject[];
  recordingId: string;
  userId: string;
  screenshots: string[];
  // Per-app active seconds within this one occurrence (folded from the backend step's
  // `appSeconds`). Absent on terminals and on steps the pipeline didn't attribute.
  appSeconds?: Record<string, number>;
  // Seconds per step-type category id (the backend step's `stepTypeSeconds`). Absent on terminals
  // and on analyses that predate classification.
  stepTypeSeconds?: Record<string, number>;
};

export type ProcessGraphNode = ProcessNode & {
  instances: ProcessNodeOccurrence[];
};

export type ProcessEdge = {
  from: string;
  to: string;
  // Total traversals across the selected instances. Repeated traversals within one instance
  // count separately.
  transitionsCount: number;
  // Distinct instances taking the transition at least once — what "N cases" means. Hand-built
  // edges may leave it out, and readers fall back to the traversal count.
  casesCount?: number;
  // Sum/count stay separate so the graph can aggregate repeated transitions without retaining
  // every occurrence. Missing timestamp coverage simply does not contribute an observation.
  waitSecondsTotal?: number;
  waitObservations?: number;
};

export type ProcessTransition = Pick<ProcessEdge, "from" | "to"> & {
  /** The box the navigation wave travels toward; `from`/`to` retain the real process edge. */
  destination: string;
  /** Position of the destination within the process instance being navigated. */
  destinationPosition?: { current: number; total: number };
};

export type ProcessRenderGraph = {
  start: string;
  end: string;
  nodes: ProcessGraphNode[];
  edges: ProcessEdge[];
};

export type ProcessInstancePath = {
  nodeIds: Set<string>;
  edgeKeys: Set<string>;
};

/** Part of the map a panel tab is pointing at. */
export type MapFocus = ProcessInstancePath;

/** The pooled nodes and edges traversed by one instance, including its entry and exit. */
export const processInstancePath = (
  instance: ProcessInstance,
  graph: Pick<ProcessRenderGraph, "start" | "end">,
): ProcessInstancePath => {
  const stepNodeIds = instance.steps.map(step => step.nodeId);
  const route = [graph.start, ...stepNodeIds, graph.end];
  return {
    nodeIds: new Set(route),
    edgeKeys: new Set(route.slice(1).map((to, index) => edgeKey(route[index], to))),
  };
};

// How the map tints step cells: "none" leaves them neutral, "time" applies the duration heat ramp.
export type ColorMode = "none" | "time";

// What a step cell prints under its name. "auto" follows how the map is being read: how many cases
// reach the step while it is read as traffic, how often and how long the step runs otherwise.
export type StepReading = "auto" | "visitsAvg" | "totalTime" | "cases" | "none";

export const resolveStepReading = (
  reading: StepReading,
  readAsTraffic: boolean,
): Exclude<StepReading, "auto"> =>
  reading !== "auto" ? reading : readAsTraffic ? "cases" : "visitsAvg";

// The detail panel reflects a single selection: a step node, a transition (edge), or nothing
// (the process overview). Edges store from/to rather than a packed key so the panel can resolve
// both endpoints without parsing ids that may themselves contain separators.
export type ProcessOccurrenceSelection = {
  instanceId: string;
  stepIndex: number;
};

export type Selection =
  | { kind: "node"; id: string; occurrence?: ProcessOccurrenceSelection }
  | { kind: "edge"; from: string; to: string };

export type EdgeSelection = Extract<Selection, { kind: "edge" }>;

export const occurrenceCount = (node: ProcessGraphNode): number => node.instances.length;

/** Cases that perform the step at least once. */
export const caseCount = (node: ProcessGraphNode): number =>
  new Set(node.instances.map(occurrence => occurrence.instanceId)).size;

export const totalDuration = (node: ProcessGraphNode): number =>
  node.instances.reduce((sum, instance) => sum + instance.duration, 0);

export const avgDuration = (node: ProcessGraphNode): number => {
  const count = occurrenceCount(node);
  return count > 0 ? totalDuration(node) / count : 0;
};

/** Non-negative wall-clock pause between consecutive steps, or null without usable timestamps. */
export const transitionWaitSeconds = (from: ProcessStep, to: ProcessStep): number | null => {
  const fromEnd = Date.parse(from.endTimestamp ?? "");
  const toStart = Date.parse(to.startTimestamp ?? "");
  if (!Number.isFinite(fromEnd) || !Number.isFinite(toStart)) return null;
  return Math.max(0, (toStart - fromEnd) / 1000);
};

/**
 * The wall-clock pauses inside one run: entry `i` is the gap between step `i` and the one after
 * it. Null where the analysis lacks the timestamps to know either way, and zero-length pauses are
 * dropped too — a gap nobody waited through is not a finding.
 */
export const stepWaitSeconds = (steps: ProcessStep[]): (number | null)[] =>
  steps.map((step, index) => {
    const next = steps[index + 1];
    const seconds = next ? transitionWaitSeconds(step, next) : null;
    return seconds !== null && seconds > 0 ? seconds : null;
  });

/** Wall-clock span of one case, from its first step starting to its last step ending. */
export const caseElapsedSeconds = (instance: ProcessInstance): number | null => {
  const start = Date.parse(instance.steps[0]?.startTimestamp ?? "");
  const end = Date.parse(instance.steps[instance.steps.length - 1]?.endTimestamp ?? "");
  return Number.isFinite(start) && Number.isFinite(end) ? Math.max(0, (end - start) / 1000) : null;
};

/** Where the cases' time went: active work in their steps, or pauses between one and the next. */
export const caseTimeSplit = (instances: ProcessInstance[]) => {
  let workSeconds = 0;
  let waitSeconds = 0;
  for (const instance of instances) {
    workSeconds += instance.source.avgDuration;
    for (const wait of stepWaitSeconds(instance.steps)) waitSeconds += wait ?? 0;
  }
  return { workSeconds, waitSeconds };
};

export const averageEdgeWaitSeconds = (edge: ProcessEdge): number | null => {
  const observations = edge.waitObservations ?? 0;
  return observations > 0 ? (edge.waitSecondsTotal ?? 0) / observations : null;
};

/**
 * For each instance that traverses `from` -> `to`, the step index the transition lands on — the
 * destination step, or the departure step when the destination is the END terminal. Mirrors the
 * virtual [START, ...steps, END] walk `graphWithInstances` counts edges over, so a transition
 * touching a terminal still resolves to a real step to open.
 */
export const edgeInstanceStepIndexes = (
  instances: ProcessInstance[],
  from: string,
  to: string,
): Map<string, number> => {
  const found = new Map<string, number>();
  for (const instance of instances) {
    if (instance.steps.length === 0) continue;
    const walk = [START_NODE_ID, ...instance.steps.map(step => step.nodeId), END_NODE_ID];
    for (let i = 0; i < walk.length - 1; i += 1) {
      if (walk[i] !== from || walk[i + 1] !== to) continue;
      // `walk` is offset by the START entry, so step k sits at walk[k + 1].
      const destination = i; // index of walk[i + 1] among steps
      found.set(instance.id, Math.min(Math.max(destination, 0), instance.steps.length - 1));
      break;
    }
  }
  return found;
};

type RankedTransitionWait = ProcessEdge & { averageWaitSeconds: number };

/** A transition between two steps, not a case's entry or exit. */
const isStepTransition = (edge: ProcessEdge, startNodeId: string, endNodeId: string) =>
  edge.from !== startNodeId &&
  edge.to !== startNodeId &&
  edge.from !== endNodeId &&
  edge.to !== endNodeId;

/** Longest meaningful waits among the transitions currently visible in the process map. */
export const rankedTransitionWaits = (
  edges: ProcessEdge[],
  startNodeId: string,
  endNodeId: string,
): RankedTransitionWait[] =>
  edges
    .filter(edge => isStepTransition(edge, startNodeId, endNodeId))
    .map(edge => ({ edge, averageWaitSeconds: averageEdgeWaitSeconds(edge) }))
    .filter(
      (item): item is { edge: ProcessEdge; averageWaitSeconds: number } =>
        item.averageWaitSeconds !== null &&
        item.averageWaitSeconds >= TOP_TRANSITION_WAIT_MIN_SECONDS,
    )
    .sort(
      (a, b) =>
        b.averageWaitSeconds - a.averageWaitSeconds ||
        b.edge.transitionsCount - a.edge.transitionsCount ||
        edgeKey(a.edge.from, a.edge.to).localeCompare(edgeKey(b.edge.from, b.edge.to)),
    )
    .map(({ edge, averageWaitSeconds }) => ({ ...edge, averageWaitSeconds }));

/**
 * Every observed positive wait between steps, ranked by its total contribution to waiting.
 * Include short and one-off waits so the breakdown accounts for the whole waiting total.
 */
export const rankedTransitionWaitTotals = (
  edges: ProcessEdge[],
  startNodeId: string,
  endNodeId: string,
) =>
  edges
    .filter(edge => isStepTransition(edge, startNodeId, endNodeId))
    .flatMap(edge => {
      const averageWaitSeconds = averageEdgeWaitSeconds(edge);
      return averageWaitSeconds ? [{ ...edge, averageWaitSeconds }] : [];
    })
    .sort(
      (a, b) =>
        (b.waitSecondsTotal ?? 0) - (a.waitSecondsTotal ?? 0) ||
        edgeKey(a.from, a.to).localeCompare(edgeKey(b.from, b.to)),
    );

// The occurrence's own start is what places it in the trend: falling back to the instance start
// would stack every visit of a step within one run on a single x, which reads as one long visit.
export const durationObservationsForNode = (node: ProcessGraphNode): DurationObservation[] =>
  node.instances.map(({ instanceId, stepIndex, duration, day, stepStart }) => ({
    id: `${instanceId}:${stepIndex}`,
    duration,
    date: day,
    timestamp: stepStart,
  }));

export const durationObservationsForInstances = (
  instances: ProcessInstance[],
): DurationObservation[] =>
  instances.map(instance => ({
    id: instance.id,
    duration: instance.source.avgDuration,
    date: caseDay(instance.source),
    timestamp: instance.source.traceStart,
  }));

const normalizeDescription = (description: string): string =>
  description.trim().replace(/\s+/g, " ").toLowerCase();

type ProcessStepDescription = {
  text: string;
  occurrences: number;
  totalDuration: number;
};

// Repeated instances can phrase the same step differently. Keep every distinct description, ordered
// with the most common first so the sidebar opens on a useful default without hiding the rest.
export const descriptionsForNode = (node: ProcessGraphNode): ProcessStepDescription[] => {
  const candidates = new Map<
    string,
    { text: string; occurrences: number; totalDuration: number }
  >();

  for (const instance of node.instances) {
    const text = instance.description.trim();
    if (!text) continue;

    const key = normalizeDescription(text);
    const current = candidates.get(key);
    if (current) {
      current.occurrences += 1;
      current.totalDuration += instance.duration;
      if (text.length > current.text.length) current.text = text;
    } else {
      candidates.set(key, { text, occurrences: 1, totalDuration: instance.duration });
    }
  }

  return [...candidates.values()].sort(
    (a, b) =>
      b.occurrences - a.occurrences ||
      b.totalDuration - a.totalDuration ||
      b.text.length - a.text.length ||
      a.text.localeCompare(b.text),
  );
};

const terminalOccurrence = (
  instance: ProcessInstance,
  stepIndex: number,
): ProcessNodeOccurrence => ({
  instanceId: instance.id,
  stepIndex,
  duration: 0,
  description: "",
  day: caseDay(instance.source),
  instanceStart: instance.source.traceStart,
  objects: [],
  recordingId: instance.source.recordingId,
  userId: instance.source.userId,
  screenshots: [],
});

export const graphWithInstances = (
  nodeDefinitions: ProcessNode[],
  instances: ProcessInstance[],
): ProcessRenderGraph => {
  const occurrencesByNodeId = new Map<string, ProcessNodeOccurrence[]>();
  const edgeCounts = new Map<string, ProcessEdge>();
  // The last instance counted on each edge: instances are walked one at a time, so a repeat
  // traversal within the same instance is always the most recent one seen.
  const lastInstanceByEdge = new Map<string, string>();
  let currentInstanceId = "";

  const appendOccurrence = (nodeId: string, occurrence: ProcessNodeOccurrence) => {
    const current = occurrencesByNodeId.get(nodeId);
    if (current) current.push(occurrence);
    else occurrencesByNodeId.set(nodeId, [occurrence]);
  };
  const addEdge = (from: string, to: string, waitSeconds?: number | null) => {
    const key = edgeKey(from, to);
    const existing = edgeCounts.get(key);
    const newCase = lastInstanceByEdge.get(key) !== currentInstanceId;
    lastInstanceByEdge.set(key, currentInstanceId);
    // Count every traversal, including repeated traversals of the same transition within one
    // process instance. `transitionsCount` belongs only to this derived render graph.
    if (existing) {
      existing.transitionsCount += 1;
      if (newCase) existing.casesCount = (existing.casesCount ?? 0) + 1;
      if (waitSeconds !== null && waitSeconds !== undefined) {
        existing.waitSecondsTotal = (existing.waitSecondsTotal ?? 0) + waitSeconds;
        existing.waitObservations = (existing.waitObservations ?? 0) + 1;
      }
    } else {
      edgeCounts.set(key, {
        from,
        to,
        transitionsCount: 1,
        casesCount: 1,
        ...(waitSeconds !== null && waitSeconds !== undefined
          ? { waitSecondsTotal: waitSeconds, waitObservations: 1 }
          : {}),
      });
    }
  };

  for (const instance of instances) {
    currentInstanceId = instance.id;
    appendOccurrence(START_NODE_ID, terminalOccurrence(instance, -1));

    let previous = START_NODE_ID;
    let previousStep: ProcessStep | null = null;
    for (const [stepIndex, step] of instance.steps.entries()) {
      appendOccurrence(step.nodeId, {
        instanceId: instance.id,
        stepIndex,
        duration: step.duration,
        description: step.description,
        day: caseDay(instance.source),
        instanceStart: instance.source.traceStart,
        objects: step.objects,
        recordingId: instance.source.recordingId,
        userId: instance.source.userId,
        screenshots: step.screenshots ?? [],
        ...(step.startTimestamp ? { stepStart: step.startTimestamp } : {}),
        ...(step.appSeconds ? { appSeconds: step.appSeconds } : {}),
        ...(step.stepTypeSeconds ? { stepTypeSeconds: step.stepTypeSeconds } : {}),
      });
      addEdge(
        previous,
        step.nodeId,
        previousStep ? transitionWaitSeconds(previousStep, step) : null,
      );
      previous = step.nodeId;
      previousStep = step;
    }

    appendOccurrence(END_NODE_ID, terminalOccurrence(instance, instance.steps.length));
    addEdge(previous, END_NODE_ID);
  }

  return {
    start: START_NODE_ID,
    end: END_NODE_ID,
    nodes: [
      {
        id: START_NODE_ID,
        name: "Start",
        app: null,
        instances: occurrencesByNodeId.get(START_NODE_ID) ?? [],
      },
      ...nodeDefinitions.map(node => ({
        ...node,
        instances: occurrencesByNodeId.get(node.id) ?? [],
      })),
      {
        id: END_NODE_ID,
        name: "End",
        app: null,
        instances: occurrencesByNodeId.get(END_NODE_ID) ?? [],
      },
    ],
    edges: [...edgeCounts.values()],
  };
};

export const terminalKind = (n: ProcessGraphNode, g: ProcessRenderGraph): "start" | "end" | null =>
  n.id === g.start ? "start" : n.id === g.end ? "end" : null;

/**
 * `graph`'s numbers on `routes`' connections only: a simplified map leaves most cases' routes
 * out, not their counts. Steps no route reaches are left for the layout to prune, as it does the
 * steps no case visits.
 */
export const graphOnRoutes = (
  graph: ProcessRenderGraph,
  routes: ProcessInstance[],
): ProcessRenderGraph => {
  const drawn = new Set(routes.flatMap(route => [...processInstancePath(route, graph).edgeKeys]));
  return { ...graph, edges: graph.edges.filter(edge => drawn.has(edgeKey(edge.from, edge.to))) };
};

/**
 * A map's size as drawn: distinct steps, and connections between different cells with entry
 * and exit included. Terminals and self-loops add nothing to either.
 */
export type MapSize = { steps: number; connections: number };

/** The largest map that opens with every case drawn. A simplified map stays within it too. */
export const SIMPLE_MAP_LIMIT: MapSize = { steps: 15, connections: 75 };

export const withinLimit = (size: MapSize, limit: MapSize): boolean =>
  size.steps <= limit.steps && size.connections <= limit.connections;

/**
 * Departures from each cell. Every visit leaves by exactly one connection, so these are its
 * visits — which stay the full count when a simplified map draws only some of those connections.
 */
export const departureCounts = (nodes: ProcessGraphNode[]): Record<string, number> =>
  Object.fromEntries(nodes.map(node => [node.id, occurrenceCount(node)]));

/** Share of departures from this edge's source, including process entry and exit edges. */
export const edgeDeparturePercentage = (
  edge: ProcessEdge,
  departures: Record<string, number>,
): number => Math.round((edge.transitionsCount / (departures[edge.from] ?? 1)) * 100);

export type FlowLink = {
  id: string;
  name: string;
  /** Traversals, the basis of `pct` (the same share the map's edge labels give). */
  count: number;
  /** Distinct cases taking the transition. */
  cases: number;
  pct: number;
  isLoop: boolean;
  /** Average pause before the next step starts, where timestamps allow measuring it. */
  averageWaitSeconds: number | null;
};

// Incoming/outgoing transitions for a node, each annotated with its share of that direction's
// traffic and whether it is a back-edge (loop). Drives the "Comes after" / "Leads to" lists.
const toFlowLink = (
  e: ProcessEdge,
  other: string,
  total: number,
  reversedMap: Map<string, boolean>,
  nameById: Map<string, string>,
): FlowLink => ({
  id: other,
  name: nameById.get(other) ?? other,
  count: e.transitionsCount,
  cases: e.casesCount ?? e.transitionsCount,
  pct: Math.round((e.transitionsCount / total) * 100),
  averageWaitSeconds: averageEdgeWaitSeconds(e),
  isLoop: e.from === e.to || (reversedMap.get(edgeKey(e.from, e.to)) ?? false),
});

const flowLinks = (
  list: ProcessEdge[],
  other: (e: ProcessEdge) => string,
  reversedMap: Map<string, boolean>,
  nameById: Map<string, string>,
): FlowLink[] => {
  const total = list.reduce((s, e) => s + e.transitionsCount, 0) || 1;
  return list
    .map(e => toFlowLink(e, other(e), total, reversedMap, nameById))
    .sort((a, b) => b.count - a.count);
};

export const buildFlow = (
  edges: ProcessEdge[],
  reversedMap: Map<string, boolean>,
  nameById: Map<string, string>,
  id: string,
): { incoming: FlowLink[]; outgoing: FlowLink[] } => {
  // A repeated step is both a departure from and an arrival at the same node. Keep it in both
  // directions so every transition list uses the same denominator as the percentages on the map.
  return {
    outgoing: flowLinks(
      edges.filter(e => e.from === id),
      e => e.to,
      reversedMap,
      nameById,
    ),
    incoming: flowLinks(
      edges.filter(e => e.to === id),
      e => e.from,
      reversedMap,
      nameById,
    ),
  };
};

/**
 * Every node's outgoing FlowLinks in one pass. The overview digest ranks all steps at once, and
 * calling `buildFlow` per step would rescan the whole edge list each time; the links themselves
 * are identical.
 */
export const outgoingFlowsByNode = (
  edges: ProcessEdge[],
  reversedMap: Map<string, boolean>,
  nameById: Map<string, string>,
): Map<string, FlowLink[]> => {
  const grouped = new Map<string, ProcessEdge[]>();
  for (const e of edges) {
    const list = grouped.get(e.from);
    if (list) list.push(e);
    else grouped.set(e.from, [e]);
  }
  return new Map(
    [...grouped].map(([id, list]) => [id, flowLinks(list, e => e.to, reversedMap, nameById)]),
  );
};

// Time share is intentionally local to the cases in which the step happened: time spent in this
// step divided by all step time across the matching, currently in-scope process instances.
// Time contribution per step = avg duration × occurrences - how much of the whole process a step
// consumes - used to rank steps and show each one's share. Terminals are excluded.
export const stepTimeStats = (graph: ProcessRenderGraph, nodes: ProcessGraphNode[]) => {
  const steps = nodes.filter(n => terminalKind(n, graph) === null);
  const time = (n: ProcessGraphNode) => totalDuration(n);
  const total = steps.reduce((s, n) => s + time(n), 0);
  const ranked = [...steps].sort((a, b) => time(b) - time(a));
  const rankById = new Map(ranked.map((n, i) => [n.id, i + 1]));
  const shareById = new Map(steps.map(n => [n.id, total > 0 ? time(n) / total : 0]));
  // `count` = distinct steps (for "#rank/count"); `occurrences` = total step
  // events across the graph (for the headline "N steps").
  const occurrences = steps.reduce((s, n) => s + occurrenceCount(n), 0);
  return { steps, ranked, rankById, shareById, count: steps.length, occurrences };
};

export type StepTimeStats = ReturnType<typeof stepTimeStats>;

/** Step id → display name, for the flow lists that only ever need the label. */
export type StepNamesById = Map<string, string>;

export type StepTableRow = {
  node: ProcessGraphNode;
  /** Cases in scope that perform the step at least once. */
  cases: number;
  occurrences: number;
  avgDuration: number;
  totalDuration: number;
};

/** One row per unique step observed across the cases in scope — never per occurrence. */
export const stepTableRows = (steps: ProcessGraphNode[]): StepTableRow[] =>
  steps.map(node => ({
    node,
    cases: caseCount(node),
    occurrences: occurrenceCount(node),
    avgDuration: avgDuration(node),
    totalDuration: totalDuration(node),
  }));

type UsualRow = StepTableRow & { share: number };

export type UsualStep = UsualRow & {
  /** Most cases doing both this step and the next one do this one first: an arrow joins them. */
  leadsToNext: boolean;
};

/**
 * How work usually goes: the few steps most cases do — at least a tenth of them — in the order
 * they come. Two steps are ordered only by the cases that do both, so a case doing one of them
 * alone has no say. A step usually leads to another where enough cases do both and most of them
 * do it first; it is laid out after every step that usually leads to it, right after the one
 * before it where that one does, so a run of steps cases do in turn stays together, joined by
 * arrows. Steps without such a run between them rank by how many others they precede more often
 * than they follow. Where no step leads to another, the steps rarely share a case and have no
 * order to show, so they stand most common first.
 */
export const usualSteps = (
  steps: ProcessGraphNode[],
  instances: ProcessInstance[],
): UsualStep[] => {
  const rows: UsualRow[] = stepTableRows(steps)
    .map(row => ({ ...row, share: instances.length > 0 ? row.cases / instances.length : 0 }))
    .filter(row => row.share >= USUAL_STEP_MIN_SHARE)
    .sort((a, b) => b.cases - a.cases || b.totalDuration - a.totalDuration)
    .slice(0, USUAL_STEP_LIMIT);
  // Where each case first reaches each step.
  const firstById = new Map(
    rows.map(row => {
      const first = new Map<string, number>();
      for (const { instanceId, stepIndex } of row.node.instances) {
        first.set(instanceId, Math.min(first.get(instanceId) ?? stepIndex, stepIndex));
      }
      return [row.node.id, first];
    }),
  );
  const firstOf = (id: string) => firstById.get(id) ?? new Map<string, number>();
  // Of the cases doing both steps, how many reach `a` first and how many reach `b` first.
  const votes = (a: string, b: string) => {
    const firstB = firstOf(b);
    let before = 0;
    let after = 0;
    for (const [instanceId, index] of firstOf(a)) {
      const other = firstB.get(instanceId);
      if (other === undefined) continue;
      if (index < other) before += 1;
      else after += 1;
    }
    return { before, after };
  };
  const minCases = Math.max(
    USUAL_ORDER_MIN_CASES,
    Math.ceil(instances.length * USUAL_ORDER_MIN_CASE_SHARE),
  );
  // The steps each step usually leads to.
  const leadsTo = new Map(
    rows.map(row => {
      const usuallyAfter = rows.filter(other => {
        if (other === row) return false;
        const { before, after } = votes(row.node.id, other.node.id);
        return before + after >= minCases && before >= (before + after) * USUAL_ORDER_MIN_AGREEMENT;
      });
      return [row.node.id, new Set(usuallyAfter.map(other => other.node.id))];
    }),
  );
  const leads = (from: UsualRow | undefined, to: UsualRow | undefined) =>
    from !== undefined && to !== undefined && (leadsTo.get(from.node.id)?.has(to.node.id) ?? false);
  // The steps a step comes first against, less those it comes second against.
  const lead = (id: string) =>
    rows.reduce((sum, other) => {
      if (other.node.id === id) return sum;
      const { before, after } = votes(id, other.node.id);
      return sum + Math.sign(before - after);
    }, 0);
  const lastIndexById = new Map(
    instances.map(instance => [instance.id, Math.max(1, instance.steps.length - 1)]),
  );
  // Where cases first reach the step, from 0 at their start to 1 at their end: the tie-break.
  const position = (id: string) =>
    median(
      [...firstOf(id)]
        .map(([instanceId, index]) => index / (lastIndexById.get(instanceId) ?? 1))
        .sort((a, b) => a - b),
    );
  const remaining = rows
    .map(row => ({ row, lead: lead(row.node.id), position: position(row.node.id) }))
    .sort(
      (a, b) =>
        b.lead - a.lead ||
        a.position - b.position ||
        b.row.cases - a.row.cases ||
        a.row.node.id.localeCompare(b.row.node.id),
    )
    .map(({ row }) => row);
  const ordered: UsualRow[] = [];
  while (remaining.length > 0) {
    // Steps whose usual predecessors are all laid out; any step, should those form a loop.
    const ready = remaining.filter(row =>
      rows.every(other => !leads(other, row) || ordered.includes(other)),
    );
    const pool = ready.length > 0 ? ready : remaining;
    const last = ordered[ordered.length - 1];
    const next = pool.find(row => leads(last, row)) ?? pool[0];
    ordered.push(next);
    remaining.splice(remaining.indexOf(next), 1);
  }
  const joined = ordered.map((row, index) => leads(row, ordered[index + 1]));
  return joined.some(Boolean)
    ? ordered.map((row, index) => ({ ...row, leadsToNext: joined[index] }))
    : rows.map(row => ({ ...row, leadsToNext: false }));
};
